import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, UserPlanInfo } from '../services/api';
import { useStorage } from '../contexts/StorageContext';
import { PricingPopup } from './PricingPopup';
import './StorageUsage.css';

interface StorageQuota {
  limit_bytes: number;
  used_bytes: number;
  available_bytes: number;
  percentage_used: number;
}

interface StorageUsageProps {
  onStorageUpdate?: (quota: StorageQuota) => void;
}

export function StorageUsage({ onStorageUpdate }: StorageUsageProps) {
  const { t } = useTranslation();
  const [quota, setQuota] = useState<StorageQuota | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPricingPopup, setShowPricingPopup] = useState(false);
  const [planInfo, setPlanInfo] = useState<UserPlanInfo | null>(null);
  const { storageVersion } = useStorage();

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const fetchStorageUsage = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.storage.getUsage();
      
      if (response.success && response.quota) {
        setQuota(response.quota);
        if (onStorageUpdate) {
          onStorageUpdate(response.quota);
        }
      } else {
        setError(response.error || t('storageUsage.loadingError'));
      }
    } catch (err) {
      console.error('Error fetching storage usage:', err);
      setError(t('storageUsage.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanInfo = async () => {
    try {
      const info = await api.subscriptions.getMyPlan();
      setPlanInfo(info);
    } catch (err) {
      console.error('Error fetching plan info:', err);
    }
  };

  useEffect(() => {
    fetchStorageUsage();
    fetchPlanInfo();
  }, []);

  // Refresh when storage version changes (triggered by file operations)
  useEffect(() => {
    if (storageVersion > 0) {
      fetchStorageUsage();
    }
  }, [storageVersion]);

  // Refresh storage usage when component receives focus or becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchStorageUsage();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleUpgradeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowPricingPopup(true);
  };

  const handlePlanChange = () => {
    // Refresh both storage usage and plan info after plan change
    fetchStorageUsage();
    fetchPlanInfo();
  };

  if (loading) {
    return (
      <div className="storage-usage-card loading">
        <div className="storage-content">
          <div className="storage-circle-container">
            <div className="storage-loading">{t('storageUsage.loading')}</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !quota) {
    return (
      <div className="storage-usage-card error">
        <div className="storage-content">
          <div className="storage-error">
            {error || t('storageUsage.error')}
          </div>
        </div>
      </div>
    );
  }

  const usedFormatted = formatBytes(quota.used_bytes);
  const limitFormatted = formatBytes(quota.limit_bytes);
  const progressPercentage = Math.min(quota.percentage_used, 100);

  return (
    <>
      <div className="storage-usage-card">
        <div className="storage-content">
          <div className="storage-circle-container">
            <svg className="storage-circle" viewBox="0 0 36 36">
              <path
                className="storage-circle-bg"
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path
                className="storage-circle-progress"
                strokeDasharray={`${progressPercentage}, 100`}
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
              />
            </svg>
            <div className="storage-percentage-center">
              <span className="percentage-number">{Math.round(progressPercentage)}</span><span className="percentage-symbol">%</span>
            </div>
          </div>
          
          <div className="storage-info">
            <div className="storage-title">{t('storageUsage.title')}</div>
            <div 
              className="storage-details"
              dangerouslySetInnerHTML={{
                __html: t('storageUsage.details', { 
                  availableSpace: `<span class="cloud-storage-value">${formatBytes(quota.available_bytes)}</span>` 
                })
              }}
            />
          </div>
        </div>
        
        <button 
          onClick={handleUpgradeClick}
          className="storage-buy-credits-button"
        >
          <span className="storage-buy-credits-text">{t('storageUsage.button')}</span>
        </button>
      </div>

      {showPricingPopup && createPortal(
        <PricingPopup 
          isOpen={showPricingPopup}
          onClose={() => setShowPricingPopup(false)}
          currentPlanInfo={planInfo || undefined}
          onPlanChange={handlePlanChange}
        />,
        document.body
      )}
    </>
  );
}

export default StorageUsage; 